import type { TMassiveUpdateDates, TMassiveUpdateVenues } from "@/app/[locale]/settings/page"
import type { TCompetitionResponse } from "@/mappers/competition/competition.mapper"
import type { Numberish, TDescription } from "@/models/common.model"
import type { HttpClient } from "@/services/http-common"
import type { TSyncEventsResponse } from "@/types/sync"
import { env } from "@/lib/env"
import { tryCatch } from "@/lib/try-catch"
import { CompetitionMapper } from "@/mappers/competition/competition.mapper"
import ApiServiceFactory from "@/services/api-service-factory"

const useMockData = env.NEXT_PUBLIC_MOCK_DATA === true

const delay = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export type TCompetitionData = {
  id?: string
  name: string
  code?: string
  description: Record<string, TDescription> | TDescription
  metadata: {
    discipline: string
    sportEvent: string
    modality: string
    event: string
    gender: string
    category: string
    rscCode?: string
  }
  order: number
  phaseCount?: number
  dateInfo: {
    startDate: Date | undefined
    endDate: Date | undefined
  }
  gender?: string
  venues: string[]
  style?: string
}

export type TApiCompetitionRequest = {
  name: string
  code?: string
  description: Record<string, TDescription> | TDescription
  metadata: {
    discipline: string
    sportEvent: string
    modality: string
    event: string
    gender: string
    category: string
    rscCode?: string
  }
  order: number
  phaseCount?: number
  dateInfo: {
    startDate: string | undefined
    endDate: string | undefined
  }
  gender?: string
  category?: string
  venues: string[]
  style?: string
}

export type TCompetitionDataRaw = {
  id?: string
  name: string
  code?: string
  description: Record<string, TDescription> | TDescription
  metadata: {
    discipline: string
    sportEvent: string
    modality: string
    event: string
    gender: string
    category: string
    rscCode?: string
  }
  order: number
  phaseCount?: number
  dateInfo: {
    startDate: string
    endDate: string
  }
  gender?: string
  venues: string[]
  style: {
    code: string
    description: Record<string, TDescription> | TDescription
    order: number
  }
}

export type TCompetitionByCodeResponse = {
  disciplineCode: string
  competitions: TCompetitionDataRaw[]
}

export class CompetitionService {
  private httpClient: HttpClient
  // private readonly baseUrl: string = "/competitions"

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async getAllCompetitions(): Promise<TCompetitionData[]> {
    try {
      const response = await tryCatch(this.httpClient.get<TCompetitionResponse>(`get-all`))
      const { data, error } = response

      if (error) {
        if (error.statusCode === 404) {
          console.log("Competition not found")
          return []
        }
      }

      if (!data) {
        return []
      }

      return CompetitionMapper.toDto(data)
    }
    catch (error) {
      console.error("Error al obtener las competiciones:", error)
      return [] // Retornar array vacío en caso de error
    }
  }

  async getAllCompetitionsByDiscipline(disciplineCode: string): Promise<TCompetitionData[]> {
    try {
      const response = await tryCatch(this.httpClient.get<TCompetitionByCodeResponse>(`by-discipline/${disciplineCode}`))
      const { data, error } = response

      if (error) {
        if (error.statusCode === 404) {
          console.log("Competition not found")
          return []
        }
      }

      if (!data) {
        return []
      }

      return CompetitionMapper.toDto(data.competitions)
    }
    catch (error) {
      console.error("Error al obtener las competiciones:", error)
      return [] // Retornar array vacío en caso de error
    }
  }

  async getCompetitionByRscCode(rscCode: string): Promise<TCompetitionData | null> {
    try {
      const { isSuccess, value } = await this.httpClient.get<TCompetitionResponse>(`get-by-code/${rscCode}`)

      if (!isSuccess || !value) {
        return null
      }
      // value es un array según TCompetitionResponse, pero el endpoint puede devolver 1 elemento.
      const mapped = CompetitionMapper.toDto(value)
      return Array.isArray(mapped) ? mapped[0] ?? null : mapped
    }
    catch (error) {
      console.error("Error al obtener la competición:", error)
      return null
    }
  }

  async updateCompetition(competition: TApiCompetitionRequest): Promise<TCompetitionData | null> {
    try {
      const { isSuccess, value } = await this.httpClient.put<TCompetitionResponse>(`update`, competition)

      if (!isSuccess || !value.length) {
        return null
      }

      return CompetitionMapper.toDto(value)?.[0] ?? null
    }
    catch (error) {
      console.error("Error al actualizar la competición:", error)
      throw new Error(error instanceof Error ? error.message : "Unknown error")
    }
  }

  async saveCompetition(competition: TApiCompetitionRequest): Promise<TCompetitionData | null> {
    try {
      const { isSuccess, value } = await this.httpClient.post<TCompetitionResponse>(``, competition)
      if (!isSuccess || !value.length) {
        return null
      }
      return CompetitionMapper.toDto(value)?.[0] ?? null
    }
    catch (error) {
      throw new Error(error instanceof Error ? error.message : "Unknown error")
    }
  }

  async updateVenues(competitionCode: string, venues: Numberish[]): Promise<void> {
    if (useMockData) {
      await delay(2000)
      console.log("Mock update venues:", competitionCode, venues)
    }
    else {
      try {
        await this.httpClient.put<void>(`update-venues/${competitionCode}`, { venues })
      }
      catch (error) {
        console.error("Error al actualizar las competiciones:", error)
        throw error
      }
    }
  }

  async updateDates(competitionCode: string, dates: { startDate: string | undefined, endDate: string | undefined }): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`update-dates/${competitionCode}`, dates))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las competiciones:", (error as Error).message)
      throw error
    }
  }

  async updateMasiveDates(settings: TMassiveUpdateDates): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`massive-update-dates`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las fechas masivamente:", (error as Error).message)
      throw error
    }
  }

  async updateMasiveVenues(settings: TMassiveUpdateVenues): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`massive-update-venues`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las venues masivamente:", (error as Error).message)
      throw error
    }
  }

  async deleteCompetition(competitionCode: string): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/${competitionCode}`)
    }
    catch (error) {
      console.error("Error al eliminar la competición:", error)
    }
  }

  async syncEvents(disciplineCode: string): Promise<TSyncEventsResponse> {
    const response = await this.httpClient.get<TSyncEventsResponse>(`/sync-from-gms/${disciplineCode}`, {})
    return response
  }
}

export const competitionService = ApiServiceFactory.createService(CompetitionService, "competition")

export default CompetitionService
